import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { createReview } from '../../../services/reviewService';

const ReviewModal = ({ isOpen, onClose, booking, onReviewSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp']
    },
    maxFiles: 5,
    maxSize: 5242880, // 5MB
    onDrop: (acceptedFiles) => {
      setPhotos(prev => [...prev, ...acceptedFiles]?.slice(0, 5));
    },
    onDropRejected: (fileRejections) => {
      const errors = fileRejections?.map(rejection => 
        rejection?.errors?.map(e => e?.message)?.join(', ')
      )?.join('; ');
      setError(errors);
    }
  });

  const removePhoto = (index) => {
    setPhotos(prev => prev?.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    if (!title?.trim()) {
      setError('Please enter a review title');
      return;
    }

    if (!comment?.trim()) {
      setError('Please enter your review');
      return;
    }

    setIsSubmitting(true);

    const reviewData = {
      bookingId: booking?.id,
      serviceType: booking?.type,
      serviceName: booking?.name,
      rating,
      title: title?.trim(),
      comment: comment?.trim(),
      photos
    };

    const result = await createReview(reviewData);

    setIsSubmitting(false);

    if (result?.success) {
      onReviewSubmitted?.();
      handleClose();
    } else {
      setError(result?.error || 'Failed to submit review. Please try again.');
    }
  };

  const handleClose = () => {
    setRating(0);
    setHoverRating(0);
    setTitle('');
    setComment('');
    setPhotos([]);
    setError('');
    onClose();
  };

  if (!isOpen || !booking) return null;

  const displayRating = hoverRating || rating;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Write a Review</h2>
            <p className="text-sm text-muted-foreground mt-1">{booking?.name}</p>
          </div>
          <button
            onClick={handleClose}
            className="w-10 h-10 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Booking Preview */}
          <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl">
            <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
              <Image
                src={booking?.image}
                alt={booking?.imageAlt}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-foreground truncate">{booking?.name}</h3>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                <Icon name="MapPin" size={14} />
                <span className="truncate">{booking?.location}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {booking?.type?.charAt(0)?.toUpperCase() + booking?.type?.slice(1)}
              </p>
            </div>
          </div>

          {/* Star Rating */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Overall Rating <span className="text-error">*</span>
            </label>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5]?.map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="transition-transform hover:scale-110 focus:outline-none"
                >
                  <Icon
                    name={star <= displayRating ? 'Star' : 'Star'}
                    size={32}
                    className={star <= displayRating ? 'fill-warning text-warning' : 'text-muted'}
                  />
                </button>
              ))}
              {rating > 0 && (
                <span className="ml-2 text-sm font-semibold text-foreground">
                  {rating === 1 && 'Poor'}
                  {rating === 2 && 'Fair'}
                  {rating === 3 && 'Good'}
                  {rating === 4 && 'Very Good'}
                  {rating === 5 && 'Excellent'}
                </span>
              )}
            </div>
          </div>

          {/* Review Title */}
          <div>
            <Input
              label="Review Title"
              required
              placeholder="Summarize your experience"
              value={title}
              onChange={(e) => setTitle(e?.target?.value)}
              maxLength={100}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {title?.length}/100 characters
            </p>
          </div>

          {/* Review Comment */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Your Review <span className="text-error">*</span>
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e?.target?.value)}
              placeholder="Share your experience with other travelers..."
              rows={6}
              maxLength={1000}
              required
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {comment?.length}/1000 characters
            </p>
          </div>

          {/* Photo Upload */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Add Photos (Optional)
            </label>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
              }`}
            >
              <input {...getInputProps()} />
              <Icon name="Upload" size={32} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-foreground font-medium">
                {isDragActive ? 'Drop photos here' : 'Drag & drop photos here, or click to select'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Up to 5 photos, max 5MB each (JPG, PNG, WebP)
              </p>
            </div>

            {/* Photo Previews */}
            {photos?.length > 0 && (
              <div className="grid grid-cols-5 gap-3 mt-4">
                {photos?.map((photo, index) => (
                  <div key={index} className="relative group">
                    <div className="aspect-square rounded-lg overflow-hidden border border-border">
                      <img
                        src={URL.createObjectURL(photo)}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-error text-error-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Icon name="X" size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-error/10 border border-error/20">
              <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={isSubmitting}
              disabled={isSubmitting || rating === 0 || !title?.trim() || !comment?.trim()}
              fullWidth
            >
              Submit Review
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewModal;